import {
  ClockCircleOutlined,
  DeleteOutlined,
  IdcardOutlined,
  InfoCircleOutlined,
  SaveFilled
} from "@ant-design/icons"
import { Button, Flex, Form, Modal, Skeleton, Typography } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React, { Suspense, lazy, useState } from "react"

import Icon from "../../../../Icon"
import ReactRouterPrompt from "../../../../components/ReactRouterPrompt.tsx"
import { MAIN_CONTENT_STICKY_BOTTOM_CLASS } from "../../../../components/main-content.ts"
import { ROLE_IDS } from "../../../../constants/roles.ts"
import type { User } from "../../../LoginPage/types/auth.types.ts"
import { type RequestAccountDeletionParams } from "../../api/types.ts"
import { type SettingProfileFormData } from "../../types/types.ts"
import { SettingsCard } from "../common/SettingsCard.tsx"
import { SettingsProfileFormChangedWatcher } from "./SettingsProfileFormChangedWatcher.tsx"
import { AccountActionsSection } from "./sections/AccountActionsSection.tsx"
import { AccountTimeSection } from "./sections/AccountTimeSection.tsx"
import { NotificationPreferencesSection } from "./sections/NotificationPreferencesSection.tsx"
import { PasswordSecurityFormSection } from "./sections/PasswordSecurityFormSection.tsx"
import { PersonalInformationFormSection } from "./sections/PersonalInformationFormSection.tsx"
import { PrivacySettingsFormSection } from "./sections/PrivacySettingsFormSection.tsx"
import { SettingsAboutSection } from "./sections/SettingsAboutSection.tsx"

const PersonalInformationFormSubcontractors = lazy(
  () => import("./sections/roles/PersonalInformationFormSubcontractors")
)

const PersonalInformationFormCustomers = lazy(
  () => import("./sections/roles/PersonalInformationFormCustomers")
)

interface SettingsProfileFormProps {
  form: FormInstance<SettingProfileFormData>
  initialValues: SettingProfileFormData
  saving: boolean
  onFinish: (data: SettingProfileFormData) => void
  onReset: () => void
  roleId: number
  userId: string
  canDeleteAccount: boolean
  accountDeletionRequestedAt?: string | null
  deleteAccountFetching: boolean
  onRequestAccountDeletion: (
    params: RequestAccountDeletionParams
  ) => Promise<unknown>
  GCCOwners?: User[]
}

export function SettingsProfileForm({
  form,
  onReset,
  initialValues,
  saving,
  onFinish,
  roleId,
  userId,
  canDeleteAccount,
  accountDeletionRequestedAt,
  deleteAccountFetching,
  onRequestAccountDeletion,
  GCCOwners
}: SettingsProfileFormProps) {
  const [isDirty, setIsDirty] = useState(false)

  const getAdditionalSection = () => {
    switch (roleId) {
      case ROLE_IDS.SUBCONTRACTOR:
        return (
          <Suspense fallback={<Skeleton.Node className={"w-full h-80"} />}>
            <SettingsCard
              iconWrapperClassName="bg-blue-100"
              icon={<IdcardOutlined className={"text-blue-600"} />}
              title="Business Details"
            >
              <PersonalInformationFormSubcontractors form={form} />
            </SettingsCard>
          </Suspense>
        )
      case ROLE_IDS.CUSTOMER:
        return (
          <Suspense fallback={<Skeleton.Node className={"w-full h-80"} />}>
            <SettingsCard
              iconWrapperClassName="bg-blue-100"
              icon={<IdcardOutlined className={"text-blue-600"} />}
              title="Personal Details"
            >
              <PersonalInformationFormCustomers />
            </SettingsCard>
          </Suspense>
        )
    }
  }

  return (
    <>
      <ReactRouterPrompt when={isDirty}>
        {({ isActive, onConfirm, onCancel }) => (
          <Modal
            open={isActive}
            title="Are you sure you want to leave?"
            onOk={onConfirm}
            onCancel={onCancel}
            okText="Leave"
            cancelText="Stay"
          >
            <p>You have unsaved changes. If you leave, they will be lost.</p>
          </Modal>
        )}
      </ReactRouterPrompt>

      <Form
        form={form}
        clearOnDestroy
        onFinish={onFinish}
        layout="vertical"
        className={"flex-1 flex gap-3 flex-col w-full with-default-error"}
        validateTrigger={["onSubmit", "onChange"]}
        disabled={saving}
        initialValues={initialValues}
      >
        <SettingsProfileFormChangedWatcher
          form={form}
          initialValues={initialValues}
          setIsDirty={setIsDirty}
          saving={saving}
        />
        <div className="lg:columns-2 space-y-3 flex-1">
          <SettingsCard
            iconWrapperClassName="bg-blue-100"
            icon={<Icon type={"User"} className={"text-blue-600"} />}
            title="Personal Information"
          >
            <PersonalInformationFormSection
              form={form}
              initialValues={initialValues}
            />
          </SettingsCard>

          {getAdditionalSection()}

          <SettingsCard
            iconWrapperClassName="bg-red-100"
            icon={<Icon type={"Shield"} className="text-red-600" />}
            title="Password & Security"
          >
            <PasswordSecurityFormSection />
          </SettingsCard>

          <SettingsCard
            iconWrapperClassName="bg-blue-100"
            icon={<ClockCircleOutlined className="text-blue-600" />}
            title="Time & Date"
          >
            <AccountTimeSection />
          </SettingsCard>

          <SettingsCard
            iconWrapperClassName="bg-orange-100"
            icon={<Icon type={"Bell"} className="text-orange-600" />}
            title="Notification Preferences"
          >
            <NotificationPreferencesSection />
          </SettingsCard>

          <SettingsCard
            iconWrapperClassName="bg-purple-100"
            icon={<InfoCircleOutlined className="text-purple-600" />}
            title="Information Notice"
          >
            <PrivacySettingsFormSection />
          </SettingsCard>

          <SettingsCard
            iconWrapperClassName="bg-red-100"
            icon={<DeleteOutlined className="text-red-600" />}
            title="Account actions"
          >
            <AccountActionsSection
              userId={userId}
              canDeleteAccount={canDeleteAccount}
              accountDeletionRequestedAt={accountDeletionRequestedAt}
              deleteAccountFetching={deleteAccountFetching}
              onRequestAccountDeletion={onRequestAccountDeletion}
            />
          </SettingsCard>

          <SettingsCard
            iconWrapperClassName="bg-gray-100"
            icon={<InfoCircleOutlined className="text-gray-600" />}
            title="About"
          >
            <SettingsAboutSection GCCOwners={GCCOwners} />
          </SettingsCard>
        </div>
        <Flex
          vertical
          gap={"small"}
          justify={"space-between"}
          className={`lg:flex-row max-lg:items-center ${MAIN_CONTENT_STICKY_BOTTOM_CLASS} z-[2] bg-background py-2`}
        >
          <span />
          {/*<Flex*/}
          {/*  gap={"small"}*/}
          {/*  align={"center"}*/}
          {/*  className={"max-lg:w-full max-lg:justify-between "}*/}
          {/*>*/}
          {/*  <Button disabled>Reset to Defaults</Button>*/}
          {/*  <Typography.Text type={"secondary"}>*/}
          {/*    Last saved: Today at {dayjs().format("h:mm A")}*/}
          {/*  </Typography.Text>*/}
          {/*</Flex>*/}

          <Flex
            gap={"small"}
            className={"max-lg:w-full max-lg:justify-between "}
          >
            <Button disabled={!isDirty || saving} onClick={onReset}>
              Cancel Changes
            </Button>
            <Button
              htmlType="submit"
              disabled={!isDirty}
              loading={saving}
              icon={<SaveFilled />}
              type={"primary"}
            >
              Save Changes
            </Button>
          </Flex>
        </Flex>
      </Form>
    </>
  )
}
